import { useId, useRef, useState } from 'react';

import { ChevronDownIcon, CloseBoldV2Icon } from '@utilities';

import styles from './_index.module.scss';

interface HierarchyPromptInputProps {
  ariaInvalid: boolean;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  promptKey: string;
  value: string;
}

const HierarchyPromptInput = ({
  ariaInvalid,
  onChange,
  options,
  placeholder,
  promptKey,
  value,
}: HierarchyPromptInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const listboxId = `${generatedId}-options`;
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const matchingOptions = searchTerm
    ? options.filter((option) => option.replace(/[\s_]/g, '').toLowerCase().includes(searchTerm))
    : options;
  const isOptionsVisible = isOpen && matchingOptions.length > 0;
  const activeOptionId = `${listboxId}-${activeOptionIndex}`;

  const closeOptions = () => {
    setIsOpen(false);
    setActiveOptionIndex(0);
    setSearchTerm('');
  };

  const selectOption = (option: string) => {
    onChange(`[${option}]`);
    closeOptions();
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (matchingOptions.length === 0) return;

      if (!isOpen) {
        setIsOpen(true);
        setActiveOptionIndex(event.key === 'ArrowDown' ? 0 : matchingOptions.length - 1);
        return;
      }

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveOptionIndex(
        (currentIndex) =>
          (currentIndex + direction + matchingOptions.length) % matchingOptions.length
      );
      return;
    }

    if (event.key === 'Enter' && isOpen && matchingOptions.length > 0) {
      event.preventDefault();
      selectOption(matchingOptions[activeOptionIndex]);
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      closeOptions();
    }
  };

  return (
    <div
      className={styles['hierarchy-prompt-input']}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeOptions();
      }}
    >
      <div className={styles['hierarchy-prompt-input-control']}>
        <span aria-hidden className={styles['hierarchy-prompt-input-width-sizer']}>
          {value || placeholder}
        </span>
        <input
          aria-activedescendant={isOptionsVisible ? activeOptionId : undefined}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOptionsVisible}
          aria-haspopup="listbox"
          aria-invalid={ariaInvalid}
          aria-label={placeholder}
          className={styles['hierarchy-prompt-input-field']}
          data-testid="hierarchy-prompt-input"
          onChange={(event) => {
            const nextValue = event.target.value;

            onChange(nextValue);
            setSearchTerm(nextValue.replace(/[\s_]+|\[|\]/g, '').toLowerCase());
            setIsOpen(true);
            setActiveOptionIndex(0);
          }}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          role="combobox"
          type="text"
          value={value}
        />
        {value && (
          <button
            aria-label={`Clear ${placeholder.toLowerCase()} value`}
            className={styles['hierarchy-prompt-input-clear']}
            onClick={() => {
              onChange('');
              setSearchTerm('');
              inputRef.current?.focus();
            }}
            type="button"
          >
            <CloseBoldV2Icon />
          </button>
        )}
        <button
          aria-controls={listboxId}
          aria-expanded={isOptionsVisible}
          aria-haspopup="listbox"
          aria-label={`Choose ${placeholder.toLowerCase()} variable`}
          className={styles['hierarchy-prompt-input-trigger']}
          onClick={() => {
            setIsOpen((currentValue) => !currentValue);
            setActiveOptionIndex(0);
            setSearchTerm('');
            inputRef.current?.focus();
          }}
          type="button"
        >
          <ChevronDownIcon />
        </button>
      </div>
      {isOptionsVisible && (
        <div
          aria-label={`${placeholder} variables`}
          className={styles['hierarchy-prompt-input-options']}
          data-testid={`screener-hierarchy-options-${promptKey.replace(/_/g, '-')}`}
          id={listboxId}
          role="listbox"
        >
          {matchingOptions.map((option, index) => (
            <button
              aria-selected={value === `[${option}]`}
              className={styles['hierarchy-prompt-input-option']}
              data-active={activeOptionIndex === index ? 'true' : undefined}
              id={`${listboxId}-${index}`}
              key={option}
              onClick={() => selectOption(option)}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveOptionIndex(index)}
              role="option"
              tabIndex={-1}
              type="button"
            >
              [{option}]
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HierarchyPromptInput;
